
import { jitsiLocalStorage } from '@jitsi/js-utils/jitsi-local-storage';

import { isIosMobileBrowser } from '../base/environment/utils';
import { fetchAxiosApi } from '../base/mtApi/functions';
import { isQuotaExceededError } from '../base/util/helpers';

import logger from './logger';

let filterSupport;

/**
 * Checks context filter support.
 *
 * @returns {boolean} True if the filter is supported and false if the filter is not supported by the browser.
 */
export function checkBlurSupport() {
    if (isIosMobileBrowser()) {
        return false;
    }
    if (typeof filterSupport === 'undefined') {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        filterSupport = typeof ctx.filter !== 'undefined';

        canvas.remove();
    }

    return filterSupport;
}

/**
 * Convert blob to base64.
 *
 * @param {Blob} blob - The link to add info with.
 * @returns {Promise<string>}
 */
export const blobToData = (blob: Blob): Promise<string> =>
    new Promise(resolve => {
        const reader = new FileReader();

        reader.onloadend = () => resolve(reader.result.toString());
        reader.readAsDataURL(blob);
    });

/**
 * Convert blob to base64.
 *
 * @param {string} url - The image url.
 * @returns {Object} - Returns the converted blob to base64.
 */
export const toDataURL = async (url: string) => {
    const blob = await fetchAxiosApi(url, { responseType: 'blob' });
    const resData = await blobToData(blob);

    return resData;
};

/**
 * Resize image and adjust original aspect ratio.
 *
 * @param {Object} base64image - Base64 image extraction.
 * @param {number} width - Value for resizing the image width.
 * @param {number} height - Value for resizing the image height.
 * @returns {Promise<string>}
 *
 */
export function resizeImage(base64image: any, width: number = 1920, height: number = 1080): Promise<string> {

    // In order to work on Firefox browser we need to handle the asynchronous nature of image loading;  We need to use
    // a promise mechanism. The reason why it 'works' without this mechanism in Chrome is actually 'by accident' because
    // the image happens to be in the cache and the browser is able to deliver the uncompressed/decoded image
    // before using the image in the drawImage call.
    return new Promise(resolve => {
        const img = document.createElement('img');

        img.onload = function() {
            // Create an off-screen canvas.
            const canvas = document.createElement('canvas');

            // Set its dimension to target size.
            const context = canvas.getContext('2d');

            canvas.width = width;
            canvas.height = height;

            // Draw source image into the off-screen canvas.
            // TODO: keep aspect ratio and implement object-fit: cover.
            context.drawImage(img, 0, 0, width, height);

            // Encode image to data-uri with base64 version of compressed image.
            resolve(canvas.toDataURL('image/jpeg', 0.5));
        };
        img.src = base64image;
    });
}

/**
 * Checks if virtual background is enabled.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean} True if virtual background is enabled and false if virtual background is disabled.
 */
export function checkVirtualBackgroundEnabled(state) {
    return state['features/base/config'].disableVirtualBackground !== true;
}

/**
 * Creating a wrapper for promises on a specific time interval.
 *
 * @param {number} milliseconds - The number of milliseconds to wait the specified
 * {@code promise} to settle before automatically rejecting the returned
 * {@code Promise}.
 * @param {Promise} promise - The {@code Promise} for which automatic rejecting
 * after the specified timeout is to be implemented.
 * @returns {Promise}
 */
export function timeout(milliseconds, promise) {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            reject(new Error('408'));

            return;
        }, milliseconds);

        promise.then(resolve, reject);
    });
}

/**
 * The {@code localStorage} key under which the user's uploaded virtual background images are kept.
 */
export const CUSTOM_BACKGROUNDS_STORAGE_KEY = 'virtualBackgrounds';

/**
 * Upper bound for the serialized custom background images.
 *
 * Each uploaded image is kept as a full size base64 JPEG data URL - a few hundred KB each - and browsers give the
 * whole origin about 5MB of localStorage. Left uncapped, a handful of uploads fills the origin's storage and every
 * other feature that persists state (recent list, settings, prejoin config...) starts failing its writes with
 * QuotaExceededError. Capping the images keeps the failure contained to the feature that produces the data.
 */
const MAX_CUSTOM_BACKGROUNDS_BYTES = 2 * 1024 * 1024;

/**
 * Reads the custom background images the user has uploaded.
 *
 * @returns {Array<Object>} The stored images, oldest first. Empty if there are none or they cannot be parsed.
 */
export function getStoredCustomBackgrounds() {
    try {
        const stored = jitsiLocalStorage.getItem(CUSTOM_BACKGROUNDS_STORAGE_KEY);
        const parsed = stored && JSON.parse(stored);

        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        logger.warn('Failed to parse the stored custom virtual backgrounds', error);

        return [];
    }
}

/**
 * Persists the custom background images, evicting the oldest ones until they fit into the storage.
 *
 * @param {Array<Object>} images - The images to store, oldest first.
 * @returns {Array<Object>} The images that were actually stored, so the caller can reflect any eviction in its own
 * state instead of showing thumbnails that no longer survive a reload.
 */
export function persistCustomBackgrounds(images) {
    const kept = Array.isArray(images) ? images.slice() : [];

    while (kept.length) {
        const payload = JSON.stringify(kept);

        // localStorage is measured in UTF-16 code units, so a character costs 2 bytes.
        if (payload.length * 2 <= MAX_CUSTOM_BACKGROUNDS_BYTES) {
            try {
                jitsiLocalStorage.setItem(CUSTOM_BACKGROUNDS_STORAGE_KEY, payload);

                return kept;
            } catch (error) {
                if (!isQuotaExceededError(error)) {
                    logger.warn('Failed to persist the custom virtual backgrounds', error);

                    return kept;
                }
            }
        }

        // Images are appended, so the front of the list is the least recently added one.
        kept.shift();
        logger.warn('Custom virtual backgrounds do not fit in the storage; dropped the oldest image.');
    }

    jitsiLocalStorage.removeItem(CUSTOM_BACKGROUNDS_STORAGE_KEY);

    return kept;
}
